import lodash from 'lodash';

import { node, aliasNodeWithArgs, nodeWithArgs, toQuery, toMutation } from 'common/util/queryHelper';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { addApplicationMessage, addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { getDatasourceOverrides, createDatasourceOverrides } from 'institutionalAdmin/actions/institutions';

export const LOAD_DATA_SOURCES = 'LOAD_DATA_SOURCES';
export const LOAD_DATA_SOURCE_TO_EDITOR = 'LOAD_DATA_SOURCE_TO_EDITOR';
export const UNLOAD_DATA_SOURCE_EDITOR = 'UNLOAD_DATA_SOURCE_EDITOR';
export const RESET_DATA_SOURCE_EDITOR = 'RESET_DATA_SOURCE_EDITOR';
export const DATA_SOURCE_CHANGE_METADATA_PROPERTY = 'DATA_SOURCE_CHANGE_METADATA_PROPERTY';
export const DATA_SOURCE_CHANGE_METADATA_PARAMETER = 'DATA_SOURCE_CHANGE_METADATA_PARAMETER';
export const DATA_SOURCE_ADD_VALUE = 'DATA_SOURCE_ADD_VALUE';
export const DATA_SOURCE_REMOVE_VALUES = 'DATA_SOURCE_REMOVE_VALUES';
export const DATA_SOURCE_SELECT_VALUE = 'DATA_SOURCE_SELECT_VALUE';
export const DATA_SOURCE_SELECT_ALL = 'DATA_SOURCE_SELECT_ALL';
export const DATA_SOURCE_CHANGE_VALUE = 'DATA_SOURCE_CHANGE_VALUE';
export const DATA_SOURCE_DECREASE_ORDER = 'DATA_SOURCE_DECREASE_ORDER';
export const DATA_SOURCE_INCREASE_ORDER = 'DATA_SOURCE_INCREASE_ORDER';
export const DATA_SOURCE_WRITE_EDITOR_PROPERTY = 'DATA_SOURCE_WRITE_EDITOR_PROPERTY';
export const DATA_SOURCE_ERRORS = 'DATA_SOURCE_ERRORS';
export const DATA_SOURCE_WRITE_DATA_ITEMS = 'DATA_SOURCE_WRITE_DATA_ITEMS';

const dashRegex = /-/g;

export const ACTION_LOGS = {
    CREATED: 'created',
    DEACTIVATED: 'deactivated',
    RESTORED: 'restored',
    MODIFIED: 'modified',
    REMOVED: 'removed'
};

export function loadDataSourceItems(dataSourceIds) {
    return dispatch => {
        if (lodash.isEmpty(dataSourceIds)) {
            return;
        }
        const queries = lodash.map(dataSourceIds, dsId => {
            return aliasNodeWithArgs(`dataItems_${dsId.replace(dashRegex, '')}`, 'requestDataSourceItems', {
                dataSourceId: dsId
            }, [
                'value',
                'text',
                'dataType',
                'order'
            ]);
        });
        return queryGraphQL(toQuery(queries)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                //response.data is object where key is dataSourceId and value is array of dataItems
                const dataItems = lodash.mapKeys(response.data, (d, key) => {
                    return lodash.find(dataSourceIds, dsId => `dataItems_${dsId.replace(dashRegex, '')}` === key);
                });
                dispatch(writeDataSourceItems(dataItems));
            }
        });
    };
}

export function loadDataSources(institutionId, { query, type } = {}) {
    return dispatch => {
        const dataSourcesQuery = node('ComplionAdmin', [
            nodeWithArgs('institution', {
                id: institutionId,
            }, [
                nodeWithArgs('dataSources', {
                    query,
                    type
                }, [
                    'id',
                    'name',
                    'type',
                    'description',
                    'institutionId',
                    'parameters',
                ]),
            ])
        ]);

        const dataSourceAnnotations = node('dataSourceAnnotations', [
            'type',
            'text'
        ]);

        return queryGraphQL(toQuery([dataSourcesQuery, dataSourceAnnotations])).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessage('error', 'An error occurred when loading the data sources'));
            } else {
                const {
                    ComplionAdmin: {
                        institution: {
                            dataSources,
                        }
                    },
                    dataSourceAnnotations,
                } = response.data;
                dispatch(writeDataSources(dataSources, dataSourceAnnotations));
            }
        });
    };
}

export function loadDataSource(dataSourceId) {
    return async dispatch => {
        const complionAdminDataSource = node('ComplionAdmin', [
            nodeWithArgs('dataSource', {
                dataSourceId
            }, [
                'id',
                'name',
                'type',
                'description',
                'institutionId',
                'parameters'
            ])
        ]);
        dispatch(unloadDataSourceEditor());
        const descriptorDataTypes = node('descriptorDataTypes');
        const dataSourceAnnotations = node('dataSourceAnnotations', [
            'type',
            'text'
        ]);

        const query = toQuery([
            complionAdminDataSource,
            descriptorDataTypes,
            dataSourceAnnotations
        ]);
        const response = await queryGraphQL(query);

        if (response.errors) {
            dispatch(addApplicationMessage('error', 'An error occurred when loading the data sources'));
        } else {
            const {
                ComplionAdmin,
                descriptorDataTypes,
                dataSourceAnnotations,
            } = response.data;
            dispatch(writeDataSources([ComplionAdmin.dataSource]));
            dispatch(writeDataSourceToEditor(
                ComplionAdmin.dataSource,
                descriptorDataTypes,
                dataSourceAnnotations
            ));
        }
    };
}

export function initializeDataSource() {
    return dispatch => {
        const descriptorDataTypes = node('descriptorDataTypes');
        const dataSourceAnnotations = node('dataSourceAnnotations', [
            'type',
            'text'
        ]);
        return queryGraphQL(toQuery([descriptorDataTypes, dataSourceAnnotations])).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessage('error', 'An error occurred when loading the data sources'));
            } else {
                dispatch(writeDataSourceToEditor(
                    {
                        id: '',
                        name: '',
                        description: '',
                        type: '',
                        parameters: {}
                    },
                    response.data.descriptorDataTypes,
                    response.data.dataSourceAnnotations
                ));
            }
        });
    };

}

export function saveDataSource(institutionId, parametersToValidate) {
    return (dispatch, getState) => {
        const storeState = getState();
        const { dataSource, values, dataTypes } = storeState.dataSourceEditor;

        const errors = validateDataSource(dataSource, values, dataTypes, parametersToValidate);
        if (errors.hasErrors) {
            dispatch(addApplicationMessage('error', 'At least one item is incomplete'));
            dispatch(writeDataSourceErrors(errors));
            return;
        }
        const { id, name, description, type } = dataSource;
        const parametersToSave = lodash.cloneDeep(dataSource.parameters || {});
        parametersToSave.values = lodash.map(values, v => lodash.pick(v, ['value', 'text', 'dataType', 'order', 'status']));
        let parameters = JSON.stringify(parametersToSave);
        const dataSourceLogs = getDataSourceLogInput(institutionId, storeState.dataSourceEditor);
        const query = nodeWithArgs('updateDataSourceMetadata', {
            id,
            name,
            description,
            type,
            institutionId,
            parameters,
            dataSourceLogs
        }, [
            'id',
            'name',
            'description',
            'type',
            'institutionId',
            'parameters'
        ]);

        return mutateGraphQL(toMutation(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessage('error', 'An error occurred when saving the data source.'));
            } else {
                dispatch(addApplicationMessage('success', 'Data source saved successfully!'));
                dispatch(writeDataSources([response.data.updateDataSourceMetadata]));
                dispatch(writeDataSourceToEditor(response.data.updateDataSourceMetadata));
                dispatch(updateDataSourceOverride(institutionId, response.data.updateDataSourceMetadata));
                
            }
        });
    };
}

export function updateDataSourceOverride(institutionId, updatedDataSource) {
    return async (dispatch, getState) => {
        await dispatch(getDatasourceOverrides(institutionId));
        const currentOverride = getState().selectedDatasourceOverrides;
        const notify = false;
        const updatedOverrides = currentOverride.map(overide => {
            if(overide.dataSourceId === updatedDataSource.id) {
                overide.dataSourceName = updatedDataSource.name;
            }

            return overide;
        });

        // Only update DataSouce Overrides if the data source being updated has an override.
        if(currentOverride.some(el => el.dataSourceId === updatedDataSource.id)) {
            dispatch(createDatasourceOverrides(institutionId, updatedOverrides, notify));
        }
    };
}

export function unloadDataSourceEditor() {
    return {
        type: UNLOAD_DATA_SOURCE_EDITOR
    };
}

export function resetDataSourceEditor() {
    return {
        type: RESET_DATA_SOURCE_EDITOR
    };
}

export function changeMetadataProperty(property, value) {
    return {
        type: DATA_SOURCE_CHANGE_METADATA_PROPERTY,
        payload: {
            property,
            value
        }
    };
}

export function changeMetadataParameter(parameter, value) {
    return {
        type: DATA_SOURCE_CHANGE_METADATA_PARAMETER,
        payload: {
            parameter,
            value
        }
    };
}

export function addValue() {
    return {
        type: DATA_SOURCE_ADD_VALUE
    };
}

export function removeSelectedValues() {
    return {
        type: DATA_SOURCE_REMOVE_VALUES
    };
}

export function selectValue(valueIndex, value) {
    return {
        type: DATA_SOURCE_SELECT_VALUE,
        payload: {
            valueIndex,
            value
        }
    };
}

export function selectAllValues(value) {
    return {
        type: DATA_SOURCE_SELECT_ALL,
        payload: value
    };
}

export function changeValue(valueIndex, property, value) {
    return {
        type: DATA_SOURCE_CHANGE_VALUE,
        payload: {
            valueIndex,
            property,
            value
        }
    };
}

export function decreaseOrder(valueIndex) {
    return {
        type: DATA_SOURCE_DECREASE_ORDER,
        payload: {
            valueIndex
        }
    };
}

export function increaseOrder(valueIndex) {
    return {
        type: DATA_SOURCE_INCREASE_ORDER,
        payload: {
            valueIndex
        }
    };
}

function writeDataSources(dataSources, dataSourceAnnotations) {
    return {
        type: LOAD_DATA_SOURCES,
        payload: {
            dataSources,
            dataSourceAnnotations
        }
    };
}

function writeDataSourceToEditor(dataSource, descriptorDataTypes, dataSourceAnnotations) {
    return {
        type: LOAD_DATA_SOURCE_TO_EDITOR,
        payload: {
            dataSource,
            descriptorDataTypes,
            dataSourceAnnotations
        }
    };
}

function validateDataSource(dataSource, values, dataTypes, parametersToValidate) {
    const { name, description, type, parameters } = dataSource;
    const errors = {
        name: null,
        description: null,
        type: null,
        parameters: {},
        values: null,
        hasErrors: false
    };
    if (lodash.isNil(name) || lodash.isEmpty(name)) {
        errors.name = 'Name cannot be blank.';
        errors.hasErrors = true;
    }
    if (lodash.isNil(description) || lodash.isEmpty(description)) {
        errors.description = 'Description cannot be blank';
        errors.hasErrors = true;
    }
    if (lodash.isNil(type) || lodash.isEmpty(type)) {
        errors.type = 'Type cannot be blank';
        errors.hasErrors = true;
    }
    if (type === 'UserDefinedDataSource') {
        const { dataType } = parameters;
        if (!dataType || !lodash.includes(dataTypes, dataType.toUpperCase())) {
            errors.parameters.dataType = 'The selected data type is missing or not allowed.';
            errors.hasErrors = true;
        }
        const valueValidation = validateValues(values, dataType.toUpperCase());
        errors.values = valueValidation.errors;
        errors.hasErrors = errors.hasErrors || valueValidation.hasErrors;
    } else {
        lodash.forEach(parametersToValidate, key => {
            const value = parameters[key];
            if (lodash.isNil(value) || ('' === value)) {
                errors.parameters[key] = `The ${key} parameter cannot be blank.`;
                errors.hasErrors = true;
            }
        });
    }
    return errors;
}

function validateValues(values, dataType) {
    let hasErrors = false;
    const distinctValues = new Set();
    const distinctText = new Set();
    let errors = lodash.map(values, ({ value, text, order }) => {
        value = typeof value === 'string' ? value.trim() : value;
        text = text && text.trim();
        const error = {
            value: null,
            text: null,
            dataType: null,
            order: null,
            hasErrors: false
        };
        if (!isValidValue(value, dataType)) {
            error.value = 'Value cannot be blank.';
            hasErrors = true;
            error.hasErrors = true;
        } else {
            if (distinctValues.has(value)) {
                error.value = 'This value is already present';
                hasErrors = true;
                error.hasErrors = true;
            } else {
                distinctValues.add(value);
            }
        }
        if (lodash.isNil(text) || lodash.isEmpty(text)) {
            error.text = 'Text cannot be blank.';
            hasErrors = true;
            error.hasErrors = true;
        } else {
            if (distinctText.has(text)) {
                error.text = 'This value is already present';
                hasErrors = true;
                error.hasErrors = true;
            } else {
                distinctText.add(text);
            }
        }
        if (!lodash.isNumber(order) || order <= 0) {
            error.order = 'Display Order must be a positive integer.';
            hasErrors = true;
            error.hasErrors = true;
        }
        return error;
    });
    return {
        hasErrors,
        errors
    };
}

function isValidValue(value, dataType) {
    switch (dataType) {
        case 'INTEGER':
            return lodash.isInteger(value);
        case 'REAL':
            return lodash.isNumber(value);
        case 'BOOLEAN':
            return lodash.isBoolean(value);
        default:
            return !lodash.isNil(value) && !lodash.isEmpty(value);
    }
}

function writeDataSourceErrors(errors) {
    return {
        type: DATA_SOURCE_ERRORS,
        payload: errors
    };
}

function writeDataSourceEditorProperty(property, value) {
    return {
        type: DATA_SOURCE_WRITE_EDITOR_PROPERTY,
        payload: {
            property,
            value
        }
    };
}

function writeDataSourceItems(dataSourceItems) {
    return {
        type: DATA_SOURCE_WRITE_DATA_ITEMS,
        payload: dataSourceItems
    };
}

export function loadGroups(institutionId) {
    return dispatch => {
        const query = node('ComplionAdmin', [
            nodeWithArgs('institution', {
                id: institutionId,
            }, [
                node('groups', [
                    'id',
                    'displayName',
                ])
            ])
        ]);

        return queryGraphQL(toQuery(query)).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessage('error', 'An error occurred when loading this page.'));
            } else {
                const {
                    ComplionAdmin: {
                        institution: {
                            groups,
                        }
                    }
                } = response.data;
                dispatch(writeDataSourceEditorProperty('groups', groups));
            }
        });
    };
}

const getDataSourceLogInput = (institutionId, dataSourceEditor) => {
    const pickKeys = ['value', 'text', 'order', 'dataType', 'status'];
    const pickKeysHaveOriginal = [...pickKeys, 'originalValue'];
    const { values, originalValues, dataSource } = dataSourceEditor;

    if (lodash.isNil(values) || lodash.isNil(originalValues) || lodash.isNil(dataSource)) {
        return [];
    }

    const { name, description, type } = dataSource;

    if (lodash.isEmpty(values)) {
        return [
            {
                institutionId,
                name,
                description,
                type,
                action: ACTION_LOGS.CREATED,
                dataType: lodash.get(dataSource, 'parameters.dataType')
            }
        ];
    }
    const mapValuesHaveOriginal = lodash.map(values, v => lodash.pick(v, pickKeysHaveOriginal));
    const mapOriginalValues = lodash.map(originalValues, v => lodash.pick(v, pickKeys));
    const originalValuesInValues = lodash.map(values, v => lodash.pick(lodash.get(v, 'originalValue'), pickKeys));
    const removeValues = lodash.differenceWith(mapOriginalValues, originalValuesInValues, lodash.isEqual)
        .map(v => ({
            institutionId,
            name,
            description,
            type,
            action: ACTION_LOGS.REMOVED,
            ...v
        }));

    const dataSourceLogs = lodash.map(mapValuesHaveOriginal, v => {
        let { originalValue, ...restValue } = v;
        const value = lodash.pick(restValue, pickKeys);
        if (originalValue) {
            originalValue = lodash.pick(originalValue, pickKeys);
            if (!lodash.isEqual(originalValue, value)) {
                return {
                    institutionId,
                    name,
                    description,
                    type,
                    action: ACTION_LOGS.MODIFIED,
                    ...value
                };
            }
        } else {
            return {
                institutionId,
                name,
                description,
                type,
                action: ACTION_LOGS.CREATED,
                ...value
            };
        }
        return null;
    });

    const filteredValues = lodash.compact(dataSourceLogs);
    const dataSourceValues = [...filteredValues, ...removeValues];
    const dataSourceLogsInput = dataSourceValues.map(item => {
        return {
            ...item,
            value: lodash.toString(item.value),
        };
    });

    return dataSourceLogsInput;
};
